import api from './api'
import { ApiResponse } from '@/types/comum'

const BASE_URL = '/api/v1/ia'

/** Espelha `originei-ia/dto/BuscaInteligenteRequest`. */
export interface BuscaInteligenteRequest {
  query: string
  /** Restringe a busca a documentos específicos; ausente = toda a carteira do tenant. */
  documentosIds?: string[]
  topK?: number
}

/** Espelha `originei-ia/dto/ResultadoBuscaInteligente` — o que `POST /v1/ia/buscar` devolve. */
export interface ResultadoBuscaInteligente {
  documentoId: string
  titulo: string
  relevancia: number
  trecho: string
  /** ISO; pode faltar quando o documento não tem data conhecida. */
  dataDocumento?: string | null
}

export interface ResumoRequest {
  texto: string
  tamanho?: 'curto' | 'medio' | 'longo'
}

export interface ResumoResponse {
  resumo: string
  pontos_chave: string[]
  confianca: number
}

export const iaService = {
  /**
   * Busca semântica nos documentos (`POST /v1/ia/buscar`). Lista crua, sem envelope —
   * o escopo por tenant é do backend. A rota antiga `/busca-semantica` nunca existiu
   * no `originei-ia` (corrigido na Onda 8 / A16).
   */
  buscar: async (request: BuscaInteligenteRequest): Promise<ResultadoBuscaInteligente[]> => {
    const response = await api.post<ResultadoBuscaInteligente[]>(`${BASE_URL}/buscar`, request)
    return (response as unknown as ResultadoBuscaInteligente[]) ?? []
  },

  resumir: async (request: ResumoRequest): Promise<ResumoResponse> => {
    const response = await api.post<ApiResponse<ResumoResponse>>(
      `${BASE_URL}/resumir`,
      request
    )
    return response.data
  },

  analisarDocumento: async (documentoId: string): Promise<unknown> => {
    const response = await api.post<ApiResponse<unknown>>(
      `${BASE_URL}/analisar-documento/${documentoId}`
    )
    return response.data
  },

  gerar: async (prompt: string): Promise<string> => {
    const response = await api.post<ApiResponse<string>>(`${BASE_URL}/gerar`, {
      prompt,
    })
    return response.data
  },

  classificar: async (texto: string): Promise<string> => {
    const response = await api.post<ApiResponse<string>>(`${BASE_URL}/classificar`, {
      texto,
    })
    return response.data
  },

  /**
   * Rascunho da Defesa de Crédito. O agente só cita números do contexto enviado —
   * lacunas viram pontos de atenção, nunca valores inventados. O texto volta para a
   * tela: quem salva é o analista.
   */
  gerarRascunhoDefesa: async (
    contexto: DefesaCreditoContexto
  ): Promise<RascunhoDefesaCredito> => {
    return await api.post<RascunhoDefesaCredito>(`${BASE_URL}/defesa-credito/rascunho`, contexto)
  },
}

// Espelham os DTOs de originei-ia.
export interface DefesaCreditoContexto {
  numeroReserva?: number | null
  razaoSocial?: string | null
  cnpj?: string | null
  segmento?: string | null
  produto?: string | null
  valorOperacao?: number | null
  descricaoOperacao?: string | null
  posicionamentoEstrategico?: string | null
  presencaGeografica?: string | null
  receitaLiquidaAnterior?: number | null
  receitaLiquidaVigente?: number | null
  resultadoOperacionalAnterior?: number | null
  resultadoOperacionalVigente?: number | null
  patrimonioLiquido?: number | null
  endividamento?: number | null
  caixaAtual?: number | null
  contasReceber?: number | null
  sacadoNome?: string | null
  indicadoresDocumentos?: string[]
  /**
   * Reserva cujos documentos a IA lê para a defesa (15/10/2026) — a fonte principal; os
   * campos acima viram complemento.
   */
  reservaId?: string | null
  /** Documentos da reserva listados pela tela (cobre os analisados antes de a IA gravar a reserva). */
  documentosIds?: string[]
  /**
   * "Análise da Originei" redigida pelo back-office na Estruturação (entrega H, 16/10/2026) —
   * texto interno que a IA usa como contexto; nulo quando não há.
   */
  analiseOriginei?: string | null
}

/** Documento da reserva que a IA leu para redigir o rascunho. */
export interface FonteDefesa {
  documentoId: string
  tipo?: string | null
}

export interface RascunhoDefesaCredito {
  analiseFinanceira?: string | null
  teseCreditoFundamento?: string | null
  avaliacaoCedente?: string | null
  recomendacao?: string | null
  pontosPositivos?: string[]
  pontosAtencao?: string[]
  /** Vazio = nenhum documento tinha leitura de IA; o rascunho saiu só dos campos. */
  documentosConsultados?: FonteDefesa[]
}
